/**
 * Admin Marketing — SEO page health.
 *
 *   /seo/health       GET   newest run in full + summaries of recent runs
 *   /seo/health/run   POST  queue a run now (marketing.admin)
 *   /seo/agents       GET   crawler page hits + visits referred by AI assistants
 *   /seo/competitors  GET   nightly inventory readings: ours vs Jiji and Meqasa
 *   /seo/keywords     GET   keywords, search volume and rankings (ours, Jiji, Meqasa)
 *                     POST add keywords or seed the list | PATCH track | DELETE | /run POST
 *   /seo/keywords/ideas GET related searches for a seed (paid, capped per day)
 *   /seo/backlinks    GET   newest backlink snapshot + history | /refresh POST (paid, once a day)
 *   /seo/ai-answers   GET   what ChatGPT, Gemini and Google AI Mode answer, and who they name
 *   /seo/search-console GET our own Google clicks, impressions and positions | /run POST
 *                     POST add questions or seed defaults | DELETE | /run POST
 *
 * A run is a crawl of our own sitemaps and a sample of pages per template,
 * done nightly by ff-api (service/seo_health.py). Every verdict is computed
 * server-side and shown as given.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useGetQueryFn from '../use-get-query-fn';
import useGetMutationFn from '../use-get-mutation-fn';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { AdminEnvelope } from '@/client/types/admin/users-orgs';
import { MARKETING_BASE, marketingKeys } from './keys';

export type SeoVerdict = 'pass' | 'warn' | 'fail';

export interface ApiSeoCheck {
  id: string;
  ok: boolean;
  level: 'fail' | 'warn';
  detail: string;
}

export interface ApiSeoPage {
  url: string;
  template: string;
  status: number;
  verdict: SeoVerdict;
  title?: string;
  checks: ApiSeoCheck[];
}

export interface ApiSeoSitemap {
  url: string;
  template: string;
  status: number;
  urls: number;
  previous_urls?: number;
  ok: boolean;
  detail: string;
}

export interface ApiSeoSummary {
  pages: number;
  pass: number;
  warn: number;
  fail: number;
  sitemaps: number;
  sitemaps_failing: number;
  sitemap_urls: number;
  healthy: boolean;
  by_template: Record<string, { pages: number; pass: number; warn: number; fail: number }>;
  failing_checks: Record<string, number>;
}

export interface ApiSeoRun {
  id: string;
  at: string;
  finished_at?: string;
  base_url?: string;
  summary: ApiSeoSummary;
  sitemaps?: ApiSeoSitemap[];
  pages?: ApiSeoPage[];
}

export interface ApiSeoHealth {
  run: ApiSeoRun | null;
  history: ApiSeoRun[];
}

export const useSeoHealthQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoHealth>>({
    defaultErrorMessage: 'Could not load SEO page health',
    endpoint: `${MARKETING_BASE}/seo/health`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.health,
    queryFn: () => queryFn({ history: 30 }),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useRunSeoHealth = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Record<string, never>, AdminEnvelope<{ queued: boolean }>>({
    defaultErrorMessage: 'Could not start the check',
    endpoint: `${MARKETING_BASE}/seo/health/run`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    // The crawl takes a couple of minutes; refetch once it has had time to land.
    onSuccess: () => {
      setTimeout(
        () => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.health }),
        1000 * 60 * 3,
      );
    },
  });
};

export interface ApiSeoCrawler {
  family: string;
  kind: 'ai' | 'search' | 'other';
  operator: string | null;
  /** train = gathers training data, search = feeds an answer engine's index, user = fetched because a person asked. */
  purpose: 'train' | 'search' | 'user' | null;
  hits: number;
  errors: number;
  templates: Record<string, number>;
  last_day: string;
}

export interface ApiSeoAgents {
  days: number;
  crawlers: ApiSeoCrawler[];
  daily: { day: string; ai: number; search: number; other: number }[];
  totals: { ai: number; search: number; other: number };
  referrals: {
    visits: number;
    sources: { source: string; visits: number }[];
    daily: { day: string; visits: number }[];
    pages: { path: string; visits: number }[];
  };
  /** Pages that answered a crawler with 4xx, most often first. */
  missing?: { path: string; count: number; last_day: string }[];
}

export const useSeoAgentsQuery = (days: number) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoAgents>>({
    defaultErrorMessage: 'Could not load AI agent activity',
    endpoint: `${MARKETING_BASE}/seo/agents`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.agents(days),
    queryFn: () => queryFn({ days }),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export interface ApiSeoCompetitorSource {
  source: string;
  total: number;
  /** Seen by ff-prospect's crawler in the last `live_days` days. */
  live: number;
  new_7d: number;
  new_30d: number;
  live_sellers: number;
  verticals: Record<string, { live: number; new_7d: number; live_price_median: number | null }>;
}

export interface ApiSeoCompetitorSnapshot {
  day: string;
  country: string;
  live_days: number;
  as_of: string | null;
  ours: { market: number; homes: number; locals: number; total: number };
  sources: ApiSeoCompetitorSource[];
  categories: Record<string, { key: string; live: number }[]>;
  regions: Record<string, { key: string; live: number }[]>;
}

export interface ApiSeoCompetitors {
  days: number;
  competitors: string[];
  latest: ApiSeoCompetitorSnapshot | null;
  history: ({ day: string; ours: number } & Record<string, { live: number; new_7d: number } | string | number>)[];
}

export const useSeoCompetitorsQuery = (days: number) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoCompetitors>>({
    defaultErrorMessage: 'Could not load competitor readings',
    endpoint: `${MARKETING_BASE}/seo/competitors`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.competitors(days),
    queryFn: () => queryFn({ days }),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useRunSeoCompetitors = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Record<string, never>, AdminEnvelope<{ queued: boolean }>>({
    defaultErrorMessage: 'Could not start the reading',
    endpoint: `${MARKETING_BASE}/seo/competitors/run`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ['marketing', 'seo', 'competitors'] }),
        1000 * 90,
      );
    },
  });
};

export type SeoSite = 'feelyfeely' | 'jiji' | 'meqasa';
export type SeoPositions = Record<SeoSite, number | null>;

export interface ApiSeoKeyword {
  keyword: string;
  group: string | null;
  topic: string | null;
  source: string | null;
  tracked: boolean;
  /** Google monthly searches in Ghana; null when Google reports none. */
  volume: number | null;
  competition: string | null;
  cpc: number | null;
  checked?: string;
  positions?: SeoPositions;
  previous?: SeoPositions | null;
  /** Our page that ranks, when one does. */
  url?: string | null;
  leader?: { rank?: number; domain?: string; title?: string };
  ai_overview?: { present: boolean; domains: string[]; cites_us: boolean };
  questions?: string[];
  related?: string[];
}

export interface ApiSeoKeywords {
  sites: SeoSite[];
  summary: {
    keywords: number;
    tracked: number;
    checked: number;
    monthly_searches: number;
    top3: Record<SeoSite, number>;
    top10: Record<SeoSite, number>;
    top30: Record<SeoSite, number>;
    ai_overviews: number;
    ai_cites_us: number;
  };
  keywords: ApiSeoKeyword[];
}

export const useSeoKeywordsQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoKeywords>>({
    defaultErrorMessage: 'Could not load keywords',
    endpoint: `${MARKETING_BASE}/seo/keywords`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.keywords,
    queryFn: () => queryFn({}),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/** Add keywords by hand (`keywords`) or build the list from competitor supply (`seed`). */
export const useAddSeoKeywords = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { keywords?: string[]; seed?: boolean },
    AdminEnvelope<{ added: number; tracked: number; max_tracked: number }>
  >({
    defaultErrorMessage: 'Could not add keywords',
    endpoint: `${MARKETING_BASE}/seo/keywords`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: marketingKeys.seo.keywords });
      // Refreshes each idea's "in the list" mark. Free: today's lookups are stored server-side.
      queryClient.invalidateQueries({ queryKey: ['marketing', 'seo', 'keyword-ideas'] });
    },
  });
};

export const useTrackSeoKeyword = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ keyword: string; tracked: boolean }, AdminEnvelope<{ updated: number }>>({
    defaultErrorMessage: 'Could not change tracking',
    endpoint: `${MARKETING_BASE}/seo/keywords`,
    method: 'PATCH',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.keywords }),
  });
};

export const useRunSeoKeywords = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Record<string, never>, AdminEnvelope<{ queued: boolean }>>({
    defaultErrorMessage: 'Could not start the check',
    endpoint: `${MARKETING_BASE}/seo/keywords/run`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    // One paid request per tracked keyword; a full run takes a few minutes.
    onSuccess: () => {
      setTimeout(() => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.keywords }), 1000 * 60 * 4);
    },
  });
};

export interface ApiSeoKeywordIdea {
  keyword: string;
  volume: number | null;
  /** LOW, MEDIUM or HIGH: advertiser competition. */
  competition: string | null;
  cpc: number | null;
  /** 0-100: how hard it is to reach the first page. */
  difficulty: number | null;
  intent: string | null;
  in_list: boolean;
}

export interface ApiSeoKeywordIdeas {
  seed: string;
  day: string;
  lookups_today: number;
  lookups_per_day: number;
  ideas: ApiSeoKeywordIdea[];
}

/**
 * Related searches for one seed. A new seed is a paid lookup, so this runs
 * only when a seed is given and never retries on its own.
 */
export const useSeoKeywordIdeasQuery = (seed: string) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoKeywordIdeas>>({
    defaultErrorMessage: 'Could not look up keyword ideas',
    endpoint: `${MARKETING_BASE}/seo/keywords/ideas`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.keywordIdeas(seed),
    queryFn: () => queryFn({ seed }),
    enabled: seed.length > 0,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export interface ApiSeoBacklinkSummary {
  rank: number | null;
  backlinks: number | null;
  referring_domains: number | null;
  referring_main_domains: number | null;
  broken_backlinks: number | null;
  backlinks_spam_score: number | null;
}

export interface ApiSeoBacklinks {
  sites: Record<SeoSite, string>;
  latest: {
    day: string;
    sites: Record<SeoSite, ApiSeoBacklinkSummary | null>;
    ours: { domain: string; rank: number | null; backlinks: number | null; first_seen: string | null }[];
    ours_total: number;
    /** Linking domains that are not junk; `ours` lists only these. Absent on very old snapshots. */
    ours_real?: number;
    /** Auto-generated spam sites ("SEO checker", "buy traffic") that link to everything. */
    ours_junk?: number;
    ours_junk_sample?: string[];
    /** True when the snapshot predates the split, so only its 100 shown rows were sorted. */
    split_partial?: boolean;
    /** Linking to both Jiji and Meqasa, not to us. */
    gaps: { domain: string; rank: number | null; links: Record<'jiji' | 'meqasa', number | null> }[];
    failed: string[];
  } | null;
  history: ({ day: string } & Partial<Record<SeoSite, number | null>>)[];
  refreshed_today: boolean;
}

export const useSeoBacklinksQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoBacklinks>>({
    defaultErrorMessage: 'Could not load backlinks',
    endpoint: `${MARKETING_BASE}/seo/backlinks`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.backlinks,
    queryFn: () => queryFn({}),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

/** Buy today's snapshot. Paid, so the server allows one a day. */
export const useRefreshSeoBacklinks = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    Record<string, never>,
    AdminEnvelope<{ day: string; ours: number; gaps: number; failed: string[] }>
  >({
    defaultErrorMessage: 'Could not refresh backlinks',
    endpoint: `${MARKETING_BASE}/seo/backlinks/refresh`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.backlinks }),
  });
};

export type SeoAnswerEngine = 'chatgpt' | 'gemini' | 'google_ai_mode' | 'perplexity';

export interface ApiSeoAiAnswer {
  day: string;
  /** How many times the answer names each marketplace. */
  mentions: Record<string, number>;
  names_us: boolean;
  cites_us: boolean;
  sources: string[];
  excerpt: string;
}

export interface ApiSeoAiAnswers {
  engines: SeoAnswerEngine[];
  brands: string[];
  summary: {
    prompts: number;
    answers: number;
    /** Percent of the newest answers that name each marketplace. */
    share: Record<string, number>;
    /** Percent of each assistant's newest answers that name us. */
    by_engine: Record<SeoAnswerEngine, number>;
    cited: number;
  };
  trend: { day: string; answers: number; share: number }[];
  prompts: {
    prompt: string;
    group: string | null;
    source: string | null;
    answers: Partial<Record<SeoAnswerEngine, ApiSeoAiAnswer>>;
  }[];
  /** Staged goals and next steps, read off the newest answers. Absent on an older API. */
  plan?: {
    /** The first goal not yet met. */
    focus: string | null;
    goals: { id: string; title: string; why: string; current: number; target: number; unit: string; met: boolean }[];
    actions: {
      /** Stable across checks, so a done mark sticks to the same step. */
      key: string;
      kind: 'measure' | 'source' | 'rival' | 'engine' | 'cite' | 'content';
      text: string;
      domain?: string;
      answers?: number;
      to?: string;
      /** Marked done but still on the list: not yet reflected in the answers. */
      done?: { at: string; share_then: number };
    }[];
    /** Every step marked done, newest first; `open` false once it left the list. */
    log?: { key: string; text: string; at: string; share_then: number; share_now: number; open: boolean }[];
  };
}

export const useSeoAiAnswersQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoAiAnswers>>({
    defaultErrorMessage: 'Could not load AI answers',
    endpoint: `${MARKETING_BASE}/seo/ai-answers`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.aiAnswers,
    queryFn: () => queryFn({}),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useAddSeoAiPrompts = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { prompts?: string[]; seed?: boolean },
    AdminEnvelope<{ added: number; prompts: number; max_prompts: number }>
  >({
    defaultErrorMessage: 'Could not add questions',
    endpoint: `${MARKETING_BASE}/seo/ai-answers`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.aiAnswers }),
  });
};

export const useMarkSeoAiAction = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<
    { key: string; done: boolean; text?: string },
    AdminEnvelope<{ key: string; done: boolean }>
  >({
    defaultErrorMessage: 'Could not save that step',
    endpoint: `${MARKETING_BASE}/seo/ai-answers/actions`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.aiAnswers }),
  });
};

export const useRunSeoAiAnswers = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Record<string, never>, AdminEnvelope<{ queued: boolean }>>({
    defaultErrorMessage: 'Could not start the check',
    endpoint: `${MARKETING_BASE}/seo/ai-answers/run`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    // Up to a minute per answer; a full run is every question times three assistants.
    onSuccess: () => {
      setTimeout(() => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.aiAnswers }), 1000 * 60 * 10);
    },
  });
};

export interface ApiGscRow {
  /** The query, or the page URL in `top_pages`. */
  key: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

export interface ApiGscTotals {
  clicks: number;
  impressions: number;
  ctr: number;
  position: number | null;
  queries: number;
}

export interface ApiGscProperty {
  site: string;
  /** Search Console permission; null when only a stored snapshot is left. */
  permission: string | null;
  snapshot: { day: string; start: string; end: string } | null;
  previous?: { day: string; start: string; end: string } | null;
  totals?: ApiGscTotals;
  previous_totals?: ApiGscTotals | null;
  top_queries?: ApiGscRow[];
  top_pages?: ApiGscRow[];
  /** Position 8-20: one small push from page one. */
  striking_distance?: ApiGscRow[];
  /** On page one but almost never clicked: a title or snippet problem. */
  low_ctr?: ApiGscRow[];
  movers?: (ApiGscRow & { previous_position: number; change: number })[];
  tracked?: { keyword: string; position: number | null; clicks?: number; impressions?: number; ctr?: number }[];
  /** Daily clicks and impressions, oldest first; staging.feelyfeely.com split out. */
  trend?: ApiGscDay[];
}

export interface ApiGscDay {
  date: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  staging_clicks: number;
  staging_impressions: number;
}

export interface ApiSeoSearchConsole {
  sites_error: string | null;
  /** Whether the signed-in admin gets the Monday email. */
  digest_subscribed?: boolean;
  properties: ApiGscProperty[];
}

/** The property most of our search traffic is on: the one with the most impressions. */
export const mainGscProperty = (data?: ApiSeoSearchConsole) =>
  [...(data?.properties ?? [])]
    .filter((p) => p.totals)
    .sort((a, b) => (b.totals?.impressions ?? 0) - (a.totals?.impressions ?? 0))[0];

export interface ApiGscPage {
  url: string;
  host: string;
  slug: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  queries: { query: string; clicks: number; impressions: number; position: number }[];
}

export interface ApiGscPageStats {
  slugs: string[];
  pages: ApiGscPage[];
  window: { start: string; end: string } | null;
  live: ApiGscTotals | null;
}

export const useSeoSearchConsoleQuery = () => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiSeoSearchConsole>>({
    defaultErrorMessage: 'Could not load Search Console',
    endpoint: `${MARKETING_BASE}/seo/search-console`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.searchConsole,
    queryFn: () => queryFn({}),
    staleTime: 1000 * 60 * 5,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};

export const useRunSeoSearchConsole = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<Record<string, never>, AdminEnvelope<{ queued: boolean }>>({
    defaultErrorMessage: 'Could not start the pull',
    endpoint: `${MARKETING_BASE}/seo/search-console/run`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    // Free and quick: two requests per property.
    onSuccess: () => {
      setTimeout(() => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.searchConsole }), 1000 * 60);
    },
  });
};

export const useSetSeoDigest = () => {
  const queryClient = useQueryClient();
  const mutationFn = useGetMutationFn<{ subscribed: boolean }, AdminEnvelope<{ subscribed: boolean }>>({
    defaultErrorMessage: 'Could not change the weekly email',
    endpoint: `${MARKETING_BASE}/seo/search-console/digest`,
    method: 'POST',
  });
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: marketingKeys.seo.searchConsole }),
  });
};

/** One listing's Google numbers (current and previous slugs). */
export const useSeoPageStatsQuery = (eid: number, enabled = true) => {
  const queryFn = useGetQueryFn<AdminEnvelope<ApiGscPageStats>>({
    defaultErrorMessage: 'Could not load Google numbers',
    endpoint: `${MARKETING_BASE}/seo/search-console/page`,
  });
  return useQuery({
    queryKey: marketingKeys.seo.page(eid),
    queryFn: () => queryFn({ eid }),
    enabled: enabled && !!eid,
    staleTime: 1000 * 60 * 30,
    gcTime: DEFAULT_GC_TIME,
    select: (response) => response?.data,
  });
};
